import { FC, useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useGetLoggedInUserDetails } from "../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { ParticipantsType } from "../../../../api/services/client/models";
import {
  FormController,
  MuiDateFieldFormController,
  MuiLink,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextFieldWithSearchIcon,
  MuiTypography,
  OptionsDataType,
  RowProps,
} from "../../../../components";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { StatusBadge } from "../../../../components/statusBadge/StatusBadge";
import {
  MODULE_PERMISSIONS,
  PAGE_IDS,
  PAGE_URLS,
} from "../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../helpers/constants/countryDateFormat";
import {
  buildActionMenuItems,
  convertToUserFormat,
  getFullName,
  isNotEmpty,
} from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { usePermissions } from "../../../../hooks/usePermissions";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import { createGenericAccounts } from "../../createGenericAccount/siteCore/SiteCoreKeys";

interface RegisteredParticipantTableProps {
  rows: ParticipantsType[];
  genericAccountId?: string;
  isFromEditGenericAccount?: boolean;
}

const pageId = PAGE_IDS.VIEW_PARTICIPANT;
const sectionIds = {
  participantName: "participantName",
  email: "email",
  programEndDate: "programEndDate",
  participantStatus: "participantStatus",
  deliveryOfficeName: "deliveryOfficeName",
  action: "action",
};

export const RegisteredParticipantTable: FC<
  RegisteredParticipantTableProps
> = ({ rows, genericAccountId, isFromEditGenericAccount = false }) => {
  const navigate = useNavigate();
  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const { loggedInUser } = useGetLoggedInUserDetails();
  const {
    commonLabels,
    fetchLabel,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();
  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const [filteredResults, setFilteredResults] = useState([...rows]);
  const [allUpdatedRows, setAllUpdatedRows] = useState([...rows]);
  const [emailOptions, setEmailOptions] = useState<OptionsDataType[]>([]);
  const [deliveryOfficeOptions, setDeliveryOfficeOptions] = useState<
    OptionsDataType[]
  >([]);

  const getLabel = (key: keyof typeof createGenericAccounts) => {
    return fetchLabel(
      createGenericAccounts.createGenericAccountRegisteredParticipantsTableTitle,
      createGenericAccounts[key],
    );
  };

  useEffect(() => {
    const uniqueEmails = Array.from(
      new Set(rows.map((row) => row.emailId)),
    ).map((email) => ({
      value: email,
      label: email,
    }));
    setEmailOptions(uniqueEmails);

    const uniqueOffices = Array.from(
      new Set(rows.map((row) => row.deliveryOfficeName)),
    ).map((office) => ({
      value: office,
      label: office,
    }));
    setDeliveryOfficeOptions(uniqueOffices);
  }, [rows]);

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [sectionIds.participantName]: "",
      [sectionIds.email]: "",
      [sectionIds.programEndDate]: "",
      [sectionIds.participantStatus]: "",
      [sectionIds.deliveryOfficeName]: "",
    },
  });

  const { control, getValues } = methods;
  const filters = useWatch({ control });

  useEffect(() => {
    setAllUpdatedRows(rows);
    setFilteredResults(rows);
  }, [rows]);

  useEffect(() => {
    const {
      participantName,
      email,
      programEndDate,
      participantStatus,
      deliveryOfficeName,
    } = getValues();

    const filtered = allUpdatedRows.filter((row) => {
      const participantNameMatch = getFullName(
        row.participantFirstName,
        row.participantLastName,
      )
        .toLowerCase()
        .includes(participantName.toLowerCase());
      const emailMatch = email
        ? row?.emailId?.toLowerCase().includes(email.toLowerCase())
        : true;
      const programEndDateMatch = programEndDate
        ? programEndDate ===
          convertToUserFormat(row.programEndDate, userCountryDateFormat)
        : true;
      const participantStatusMatch = participantStatus
        ? row.status.toLowerCase().includes(participantStatus.toLowerCase())
        : true;
      const deliveryOfficeMatch = deliveryOfficeName
        ? row.deliveryOfficeName
            .toLowerCase()
            .includes(deliveryOfficeName.toLowerCase())
        : true;

      return (
        participantNameMatch &&
        emailMatch &&
        programEndDateMatch &&
        participantStatusMatch &&
        deliveryOfficeMatch
      );
    });

    setFilteredResults(filtered);
  }, [filters, allUpdatedRows]);

  const handleViewAccount = (id: string) => {
    navigate(PAGE_URLS.VIEW_PARTICIPANT, {
      replace: true,
      state: {
        candidateId: id,
        from: PAGE_IDS.REGISTERED_PARTICIPANT_TAB,
        genericAccountId: genericAccountId,
        isFromViewParticipant: true,
      },
    });
  };

  const handleEditParticipant = (id: string) => {
    navigate(PAGE_URLS.EDIT_PARTICIPANT, {
      replace: true,
      state: {
        candidateId: id,
        from: PAGE_IDS.REGISTERED_PARTICIPANT_TAB,
        isFromViewParticipant: false,
        isFromEditGenericAccount: isFromEditGenericAccount,
        genericAccountId: genericAccountId,
      },
    });
  };

  const columns = [
    {
      id: `${pageId}-${sectionIds.participantName}`,
      label: getLabel("participantName"),
      isSortVisible: true,
      render: (row: RowProps) => {
        const fullName = `${row.firstName ?? ""} ${row.lastName ?? ""}`.trim();
        return !fullName ? null : (
          <MuiLink onClick={() => handleViewAccount(row.candidateId)}>
            <MuiEllipseLabel
              labelTypoClassName="Link"
              text={getFullName(row.firstName, row.lastName)}
              labelTypoVariant="h3"
              placement="bottom-start"
              pointerCursor
            />
          </MuiLink>
        );
      },
    },
    {
      id: `${pageId}-${sectionIds.email}`,
      label: getLabel("emailId"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="cell">
          {row.emailId}
        </MuiTypography>
      ),
    },
    {
      id: `${pageId}-${sectionIds.programEndDate}`,
      label: getLabel("programEndDate"),
      isSortVisible: true,
      isEmptyValue: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="cell">
          {isNotEmpty(row.programEndDate)
            ? convertToUserFormat(row.programEndDate, userCountryDateFormat)
            : ""}
        </MuiTypography>
      ),
    },
    {
      id: `${pageId}-${sectionIds.participantStatus}`,
      label: getLabel("participantStatus"),
      isSortVisible: true,
      render: (row: RowProps) => (
        <MuiTypography variant="h3" className="cell">
          <StatusBadge
            label={fetchValueByKeyOfDropdown(
              createGenericAccounts.createGenericAccountRegisteredParticipantsTableTitle,
              createGenericAccounts.participantStatusList,
              row.status,
            )}
            status={row.status}
          />
        </MuiTypography>
      ),
    },
    {
      id: sectionIds.deliveryOfficeName,
      label: getLabel("deliveryOffices"),
      isSortVisible: true,
    },
    {
      id: `${pageId}-${sectionIds.action}`,
      label: getLabel("action"),
      isSortVisible: false,
      render: (row: RowProps) => {
        const registeredParticipantActions = [
          {
            permissionKey:
              MODULE_PERMISSIONS.GENERIC_ACCOUNT_EDIT,
            create: () => ({
              onClick: () => handleEditParticipant(row.candidateId),
              dataTestId: `edit-participant`,
              label: commonLabels.edit,
              ariaLabel: commonLabels.edit,
            }),
          },
        ];

        const registeredParticipantMenuItems = buildActionMenuItems(
          row,
          registeredParticipantActions,
          hasModulePermission,
        );

        return registeredParticipantMenuItems ? (
          <MuiOptionMenu menuItems={registeredParticipantMenuItems} />
        ) : null;
      },
    },
  ];

  const tableHeaderForm = [
    <FormController
      control={control}
      key={`${pageId}-${sectionIds.participantName}`}
      name={sectionIds.participantName}
    >
      <MuiTextFieldWithSearchIcon
        placeholder={commonLabels.search}
        id={`${pageId}-participantName`}
        showCloseIcon
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
        searchARIALabel={commonARIALabel.search}
      />
    </FormController>,
    <FormController
      control={control}
      key={`${pageId}-${sectionIds.email}`}
      name={sectionIds.email}
    >
      <MuiSelect
        label=""
        options={emailOptions}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <MuiDateFieldFormController
      control={control}
      key={`${pageId}-${sectionIds.programEndDate}`}
      name={sectionIds.programEndDate}
      valueFormat={userCountryDateFormat}
      format={userCountryDateFormat}
      requiredARIALabel={commonARIALabel.required}
      selectDateARIALabel={commonARIALabel.selectDate}
    />,
    <FormController
      control={control}
      key={`${pageId}-${sectionIds.participantStatus}`}
      name={sectionIds.participantStatus}
    >
      <MuiSelect
        label=""
        options={fetchDropdownValues(
          createGenericAccounts.createGenericAccountRegisteredParticipantsTableTitle,
          createGenericAccounts.participantStatusList,
        )}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      key={`${pageId}-${sectionIds.deliveryOfficeName}`}
      name={sectionIds.deliveryOfficeName}
    >
      <MuiSelect
        label=""
        options={deliveryOfficeOptions}
        visibleDefaultSelectOption
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
  ];

  return (
    <MuiTable
      ref={tableRef}
      pageId={pageId}
      tableHeaderForm={tableHeaderForm}
      columns={columns}
      tableResults={[...filteredResults]}
      noDataInApi={filteredResults.length === 0}
      priorityResults={[]}
      emptyMessageColSpan={6}
      emptyMessage={getLabel("noParticipantsRegistered")}
      noResultMessage={commonLabels.searchCriteria}
    />
  );
};
